import type { NodeAvailability, NodeDef } from '../../../core/types';
import { NODES, NODE_BY_ID } from '../../../data/nodes';
import type { ScreenCtx } from '../../types';
import {
  abyssGateOf,
  bestLevelOf,
  costExpeditionsOf,
  gateLevelOf,
  hasArtisan,
  isAbyssNode,
  isRepeatable,
  visibleNodes,
} from '../../abyssApi';
import { formatGold } from '../../format';
import { icon, type IconName } from '../../icons';
import { softHyphenate } from '../../hyphen';
import { describeNodeLevel, nodeDepth } from '../../treeApi';
import { isHintSeen, markHintSeen } from '../../tips';
import { treeRevealAll } from '../../treeReveal';
import {
  type GraphLayout,
  graphGeometry,
  layoutTree,
  nodeIconName,
  nodeShape,
  nodeVisualState,
  type NodeVisualState,
  visibleNodeIds,
} from '../../treeGraph';

/**
 * Onglet « Arbre » de l'Accalmie — refonte F1, lot 2 (v0.15.0).
 *
 * L'arbre est un **graphe unique** qui défile verticalement : la racine en
 * haut, trois **couloirs** teintés (Arsenal, Boutique, Arcanes) — et, depuis la
 * v0.46.0 (A94), un quatrième, l'**Abîme**, qui n'existe qu'une fois l'Artisan
 * des dés acquis —, des **liens**
 * SVG parent → enfant (gris pointillé tant que le parent manque, dorés dès qu'il
 * est acquis, avec une pointe vers les nœuds accessibles). La disposition est
 * **calculée** depuis `src/data/nodes.ts` (`ui/treeGraph.ts`) : aucun nœud n'est
 * placé à la main.
 *
 * Formes : carré = racine, hexagone = déblocage à un niveau, rond = bonus à
 * niveaux (badge « n/max », « Max » au maximum ; « Nv n » pour *Sans fond*, qui
 * n'a pas de maximum). États : bordure d'or acquis, mousse achetable
 * maintenant, gris or insuffisant, gris sombre + cadenas verrouillé par le
 * parent **ou par la porte de l'Abîme** (« S'ouvre au niveau N »). Dans
 * l'Abîme le lien parent → enfant n'est qu'un chemin (A94-3) : un nœud dont la
 * porte est ouverte est achetable même si le précédent ne l'est pas.
 *
 * Un appui **sélectionne** le nœud et ouvre le **panneau fixe en bas** (nom,
 * branche, « Actuel : … », « Prochain niveau : … · N or » — COMMENTS v0.4.1 §2
 * via `describeNodeLevel` —, raison de refus de `canBuyNode`) ; le bouton du
 * panneau achète. La sélection, le défilement et le « flash » du dernier achat
 * survivent au rafraîchissement complet de l'écran (événement `purchase`).
 *
 * **Dévoilé par l'achat** (v0.49.0, A98 — COMMENTS v0.48.1 §2) : au début de la
 * campagne seuls la racine et ses trois enfants se voient ; acheter un nœud
 * dévoile ses enfants (`visibleNodeIds`, `ui/treeGraph.ts`). Un nœud masqué
 * n'est ni dessiné ni cliquable, ses liens et son étiquette non plus ; la
 * disposition reste calculée sur **tous** les nœuds, donc rien ne bouge quand
 * l'un d'eux apparaît (il se fond en place après l'achat du parent, coupé en
 * mouvement réduit). L'Abîme n'est pas concerné. Le compteur « n / 64 »
 * garde le total. La case de debug « Tout l'arbre » (`ui/treeReveal.ts`)
 * montre tout.
 *
 * Sélecteurs stables (e2e) : `[data-node="<id>"]` sur chaque nœud (bouton,
 * `aria-pressed`), `[data-role="node-panel"]`, `[data-action="buy-node"]`.
 */

/** Id du nœud tout juste acheté : consommé au prochain rendu pour le « flash ». */
let lastBoughtId: string | null = null;
/** Ce premier niveau vient-il d'être acheté ? Alors ses enfants apparaissent avec un fondu. */
let lastBoughtWasNew = false;
/** Nœud sélectionné (panneau ouvert) ; survit aux reconstructions de l'onglet. */
let selectedId: string | null = null;
/** Défilement du graphe, rétabli après chaque reconstruction. */
let savedScroll = 0;

const BRANCH_LABEL: Record<string, string> = {
  root: 'Racine',
  arsenal: 'Arsenal',
  shop: 'Boutique',
  arcane: 'Arcanes',
  abyss: 'Abîme',
};

const BRANCH_ICON_NAME: Record<string, IconName> = {
  arsenal: 'die',
  shop: 'gold',
  arcane: 'spell',
  abyss: 'abyss',
};

const STATE_LABEL: Record<NodeVisualState, string> = {
  owned: 'acquis',
  buy: 'achetable',
  poor: 'or insuffisant',
  locked: 'verrouillé',
};

/**
 * Disposition calculée une fois par visibilité : elle ne dépend que des données
 * et de la présence du couloir de l'Abîme (deux dispositions en cache, A94 —
 * sans l'Artisan des dés, le couloir n'existe pas, et les trois autres gardent
 * exactement la largeur d'avant).
 */
const cachedLayouts = new Map<boolean, GraphLayout>();
function layout(withAbyss: boolean, nodes: readonly NodeDef[]): GraphLayout {
  let lay = cachedLayouts.get(withAbyss);
  if (!lay) {
    lay = layoutTree(nodes);
    cachedLayouts.set(withAbyss, lay);
  }
  return lay;
}

function reasonText(av: NodeAvailability): string {
  if (av.reason === 'locked') return `Nécessite ${av.parentName ?? '?'}`;
  if (av.reason === 'maxed') return 'Niveau maximum';
  if (av.reason === 'gold') return 'Or insuffisant';
  const gate = gateLevelOf(av);
  if (gate !== null) return `S'ouvre au niveau ${gate}`;
  return 'Indisponible';
}

function kindText(node: NodeDef): string {
  if (node.parent === null) return 'racine';
  if (isRepeatable(node)) return 'bonus sans fin';
  return node.maxLevel <= 1 ? 'déblocage' : 'bonus à niveaux';
}

/** Niveau maximum affiché : « ∞ » pour un nœud répétable (*Sans fond*). */
function maxText(node: NodeDef): string {
  return isRepeatable(node) ? '∞' : String(node.maxLevel);
}

interface NodeView {
  node: NodeDef;
  level: number;
  availability: NodeAvailability;
  state: NodeVisualState;
}

function viewOf(node: NodeDef, ctx: ScreenCtx): NodeView {
  const { game } = ctx;
  const level = game.state.nodes[node.id] ?? 0;
  const availability = game.canBuyNode(node.id);
  return { node, level, availability, state: nodeVisualState(level, availability) };
}

/**
 * Nœuds à dessiner : ceux que l'achat a dévoilés (A98), ou tous quand la case
 * de debug « Tout l'arbre » est cochée.
 */
function revealedIds(ctx: ScreenCtx, shown: readonly NodeDef[]): Set<string> {
  return visibleNodeIds(shown, ctx.game.state.nodes, { revealAll: treeRevealAll() });
}

/**
 * Vue d'un nœud pour le dessin. En **aperçu** (écran d'achat, A112) il n'y a ni
 * disponibilité ni prix : `availability` vaut `null` et l'état est `preview`
 * (ou `owned` pour un talent que le joueur possède).
 */
export interface TreeDrawView {
  node: NodeDef;
  level: number;
  availability: NodeAvailability | null;
  state: NodeVisualState | 'preview';
}

export interface TreeDrawOptions {
  /** Boutons `[data-node]` cliquables (onglet Arbre) ou simples `span` (panorama de l'écran d'achat). */
  interactive: boolean;
  /** Prix et portes sous les étiquettes (onglet Arbre) ; sinon « Nv max n » ou rien. */
  showCosts: boolean;
  flashId?: string | null;
  fadeIn?: boolean;
  selectedId?: string | null;
}

/** Disponibilité neutre des nœuds en aperçu : ni prix, ni refus. */
const PREVIEW_AVAILABILITY: NodeAvailability = { ok: false, cost: 0 };

/**
 * Graphe (couloirs, liens, nœuds, étiquettes) pour une largeur donnée — dessin
 * **partagé** par l'onglet Arbre de l'Accalmie et le panorama de l'écran d'achat
 * (A112). `shown` : les nœuds de la disposition ; `withAbyss` : clé du cache de
 * disposition (le couloir de l'Abîme existe-t-il ?) ; `revealed` : ceux qu'on
 * dessine (A98).
 */
export function drawTreeGraph(
  shown: readonly NodeDef[],
  withAbyss: boolean,
  views: ReadonlyMap<string, TreeDrawView>,
  revealed: ReadonlySet<string>,
  width: number,
  opts: TreeDrawOptions,
): string {
  const flashId = opts.flashId ?? null;
  const fadeIn = opts.fadeIn ?? false;
  const selectedNode = opts.selectedId ?? null;
  // La disposition porte sur tous les nœuds : un nœud qui apparaît ne déplace rien.
  const lay = layout(withAbyss, shown);
  const geo = graphGeometry(lay, width);
  // Fondu : les enfants du nœud tout juste acheté, s'il était à son premier niveau.
  const fading = (id: string): boolean => fadeIn && !!flashId && views.get(id)?.node.parent === flashId;

  const lanes = lay.lanes
    .map((branch, i) => {
      const ic = BRANCH_ICON_NAME[branch] ?? 'node';
      const box = geo.lanes[i];
      return `<div class="tree-lane" data-lane="${branch}" style="left:${box.left.toFixed(1)}px;width:${box.width.toFixed(1)}px">
        <span class="tree-lane-title">${icon(ic)}${BRANCH_LABEL[branch] ?? branch}</span>
      </div>`;
    })
    .join('');

  let links = '';
  for (const link of lay.links) {
    const d = geo.paths[`${link.from}>${link.to}`];
    const parent = views.get(link.from);
    const child = views.get(link.to);
    if (!d || !parent || !child) continue;
    if (!revealed.has(link.from) || !revealed.has(link.to)) continue;
    const parentOwned = parent.level > 0;
    let cls = 'is-locked';
    let marker = '';
    if (parentOwned && child.level > 0) cls = 'is-owned';
    else if (isAbyssNode(child.node)) {
      // A94-3 : dans l'Abîme, le lien n'est qu'un chemin, jamais une condition.
      cls = 'is-chain';
    } else if (parentOwned) {
      cls = child.state === 'buy' ? 'is-open is-buy' : 'is-open';
      marker = ` marker-end="url(#${child.state === 'buy' ? 'tree-arrow-buy' : 'tree-arrow'})"`;
    }
    if (flashId && link.to === flashId) cls += ' is-flash';
    if (fading(link.to)) cls += ' is-reveal';
    links += `<path class="tree-link ${cls}" d="${d}"${marker}/>`;
  }
  const svg = `<svg class="tree-links" width="${geo.width}" height="${geo.height}" viewBox="0 0 ${geo.width} ${geo.height}" aria-hidden="true" focusable="false">
    <defs>
      <marker id="tree-arrow" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse"><path d="M0 1L9 5L0 9z" class="tree-arrow"/></marker>
      <marker id="tree-arrow-buy" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse"><path d="M0 1L9 5L0 9z" class="tree-arrow tree-arrow--buy"/></marker>
    </defs>${links}</svg>`;

  let nodes = '';
  for (const id of lay.order) {
    const view = views.get(id);
    const box = geo.nodes[id];
    if (!view || !box || !revealed.has(id)) continue;
    const { node, level, state } = view;
    const availability = view.availability ?? PREVIEW_AVAILABILITY;
    const preview = view.state === 'preview' || view.availability === null;
    const shape = nodeShape(node);
    const maxed = level >= node.maxLevel;
    const selected = id === selectedNode;
    const branch = node.parent === null ? 'root' : node.branch;
    const ic = state === 'locked' ? 'lock' : nodeIconName(node);
    const repeatable = isRepeatable(node);
    const gate = gateLevelOf(availability);
    let badge = '';
    if (shape === 'round' && (!preview || level > 0)) {
      const up = state === 'owned' && !maxed && availability.ok;
      // *Sans fond* n'a pas de maximum : « Nv n », jamais « Max » (A94).
      const text = maxed ? 'Max' : repeatable ? `Nv ${level}` : `${level}/${node.maxLevel}`;
      badge = `<span class="tree-node-badge${maxed ? ' is-max' : ''}${up ? ' is-up' : ''}">${text}</span>`;
    }
    const cost = availability.cost;
    const stateText = state === 'preview' ? 'aperçu' : STATE_LABEL[state];
    const label = preview
      ? `${node.name}, ${stateText}${shape === 'round' ? `, niveau maximum ${maxText(node)}` : ''}`
      : `${node.name}, ${stateText}${
          shape === 'round' ? `, niveau ${level}${repeatable ? '' : ` sur ${node.maxLevel}`}` : ''
        }${maxed ? '' : gate !== null ? `, s'ouvre au niveau ${gate}` : `, ${formatGold(cost)} or`}`;
    const cls = [
      'tree-node',
      `tree-node--${shape}`,
      `is-${state}`,
      selected ? 'is-selected' : '',
      id === flashId ? 'is-flash' : '',
      fading(id) ? 'is-reveal' : '',
      opts.interactive ? '' : 'tree-node--static',
    ]
      .filter(Boolean)
      .join(' ');
    const shapeHtml = `<span class="tree-node-shape"><span class="tree-node-face">${icon(ic)}</span></span>${badge}`;
    if (opts.interactive) {
      nodes += `<button type="button" class="${cls}" data-node="${id}" data-state="${state}" data-branch="${branch}" data-depth="${nodeDepth(id)}" data-row="${lay.cells[id].row}"${
        node.parent ? ` data-parent="${node.parent}"` : ''
      }${maxed ? '' : ` data-cost="${Math.round(cost)}"`}${gate !== null ? ` data-gate="${gate}"` : ''} aria-pressed="${selected}" aria-label="${label}" style="left:${box.x.toFixed(1)}px;top:${box.y}px">
      ${shapeHtml}
    </button>`;
    } else {
      // Panorama en lecture seule : un `span`, ni bouton, ni `data-node`, ni focus.
      nodes += `<span class="${cls}" data-preview-node="${id}" data-state="${state}" data-branch="${branch}" role="img" aria-label="${label}" style="left:${box.x.toFixed(1)}px;top:${box.y}px">
      ${shapeHtml}
    </span>`;
    }

    let sub: string;
    if (!opts.showCosts) {
      // Prix masqués : « Nv max n » pour un bonus à niveaux, rien pour un déblocage.
      sub = shape === 'round' ? `<em class="is-locked">Nv max ${maxText(node)}</em>` : '';
    } else if (maxed) sub = '<em class="is-max">Max</em>';
    // Porte de l'Abîme fermée : le sous-titre dit à quel niveau elle s'ouvre (A94).
    else if (gate !== null) sub = `<em class="is-locked is-gate" data-role="node-gate">S'ouvre au niveau ${gate}</em>`;
    else if (state === 'locked') sub = `<em class="is-locked">${formatGold(cost)} or</em>`;
    else sub = `<em class="${availability.ok ? 'is-ok' : 'is-short'}">${formatGold(cost)} or</em>`;
    nodes += `<span class="tree-label tree-label--${branch}${state === 'locked' ? ' is-locked' : ''}${fading(id) ? ' is-reveal' : ''}${opts.interactive ? '' : ' tree-label--static'}" data-for="${id}" style="left:${(box.x - box.labelW / 2).toFixed(1)}px;top:${box.y + 25}px;width:${box.labelW}px">${softHyphenate(node.name)}${sub}</span>`;
  }

  return `<div class="tree-graph" role="group" aria-label="Arbre des talents" data-lanes="${lay.lanes.length}" style="width:${geo.width}px;height:${geo.height}px">${lanes}${svg}${nodes}</div>`;
}

/**
 * Panorama de l'arbre **complet** (A112) : les 64 nœuds, Abîme compris, tous
 * dévoilés, en lecture seule. `levels` : niveaux possédés par le joueur (ses
 * nœuds sont `owned`, les autres en aperçu neutre). Sert l'écran d'achat.
 */
export function panoramaHtml(levels: Readonly<Record<string, number>>, width: number): string {
  const views = new Map<string, TreeDrawView>();
  for (const node of NODES) {
    const level = levels[node.id] ?? 0;
    views.set(node.id, { node, level, availability: null, state: level > 0 ? 'owned' : 'preview' });
  }
  const revealed = new Set(NODES.map((n) => n.id));
  return drawTreeGraph(NODES, true, views, revealed, width, { interactive: false, showCosts: false });
}

/** Graphe de l'onglet Arbre (couloirs, liens, nœuds, étiquettes) pour une largeur donnée. */
function graphHtml(ctx: ScreenCtx, width: number, flashId: string | null, fadeIn: boolean): string {
  const shown = visibleNodes(ctx.game);
  const views = new Map<string, TreeDrawView>(shown.map((n) => [n.id, viewOf(n, ctx)]));
  return drawTreeGraph(shown, hasArtisan(ctx.game), views, revealedIds(ctx, shown), width, {
    interactive: true,
    showCosts: true,
    flashId,
    fadeIn,
    selectedId,
  });
}

/** Panneau fixe du nœud sélectionné (ou invite quand rien n'est choisi). */
function panelHtml(ctx: ScreenCtx): string {
  const node = selectedId ? NODE_BY_ID[selectedId] : undefined;
  if (!node) {
    // A88-9 : l'invite disparaît dès qu'un nœud a été touché une fois (par installation).
    if (isHintSeen('treeHint')) return '';
    return `<p class="node-panel-hint">${icon('node')}<span>Touchez un nœud pour voir son effet et l'acheter.</span></p>`;
  }
  const { game } = ctx;
  const { level, availability, state } = viewOf(node, ctx);
  const maxed = availability.reason === 'maxed';
  const described = describeNodeLevel(game, node.id, level);
  const current = described?.current ?? node.description;
  const shape = nodeShape(node);
  const branch = node.parent === null ? 'root' : node.branch;
  const cost = formatGold(availability.cost);
  // A94 : le prix d'un niveau de l'Abîme est en or, suivi du nombre d'Expéditions
  // du niveau de sa porte qu'il représente (« · 3 Expéditions du niveau 13 »).
  const expeditions = costExpeditionsOf(node);
  const gateOfNext = expeditions !== null ? abyssGateOf(game, node.id) : null;
  const expeditionsText =
    expeditions !== null && gateOfNext
      ? ` · <span data-role="node-expeditions">${expeditions} Expédition${expeditions > 1 ? 's' : ''} du niveau ${gateOfNext.level}</span>`
      : '';
  const nextLine = maxed
    ? `<p class="node-panel-line node-panel-line--max" data-role="node-next"><span class="node-panel-k">Prochain niveau</span><span>Niveau maximum atteint.</span></p>`
    : `<p class="node-panel-line" data-role="node-next"><span class="node-panel-k">Prochain niveau</span><span>${
        described?.next ? `${described.next} · ` : ''
      }<b class="${availability.reason === 'gold' ? 'is-short' : ''}">${cost} or</b>${expeditionsText}</span></p>`;
  let reason = '';
  const gate = gateLevelOf(availability);
  if (availability.reason === 'gold') {
    const missing = Math.max(0, availability.cost - game.state.gold);
    reason = `<p class="node-panel-reason" data-role="node-reason">Il manque ${formatGold(missing)} or.</p>`;
  } else if (availability.reason === 'locked') {
    reason = `<p class="node-panel-reason" data-role="node-reason">${icon('lock')}<span>Achetez d'abord « ${availability.parentName ?? '?'} ».</span></p>`;
  } else if (gate !== null) {
    reason = `<p class="node-panel-reason" data-role="node-reason" data-gate="${gate}">${icon('lock')}<span>S'ouvre au niveau ${gate} (meilleur niveau atteint : ${bestLevelOf(game)}).</span></p>`;
  }
  const button = availability.ok
    ? `<button type="button" class="f-btn f-btn--buy f-btn--block btn-node" data-action="buy-node">Acheter · ${cost} or</button>`
    : `<button type="button" class="f-btn f-btn--block btn-node" data-action="buy-node" disabled>${reasonText(availability)}</button>`;
  return `
    <button type="button" class="f-ibtn node-panel-close" data-action="close-node" aria-label="Fermer le détail du nœud">${icon('close')}</button>
    <div class="node-panel-head">
      <span class="node-panel-shape tree-node--${shape} is-${state}" aria-hidden="true"><span class="tree-node-shape"><span class="tree-node-face">${icon(
        state === 'locked' ? 'lock' : nodeIconName(node),
      )}</span></span></span>
      <div class="node-panel-title">
        <h3 class="node-panel-name" data-role="node-name">${node.name}</h3>
        <p class="node-panel-meta">${BRANCH_LABEL[branch] ?? branch} · ${kindText(node)} · <b class="node-level">Nv ${level}/${maxText(node)}</b></p>
      </div>
    </div>
    <p class="node-panel-line" data-role="node-current"><span class="node-panel-k">Actuel</span><span>${current}</span></p>
    ${nextLine}
    ${reason}
    ${button}
  `;
}

export function renderTreeTab(container: HTMLElement, ctx: ScreenCtx): void {
  const { game } = ctx;
  if (selectedId && !NODE_BY_ID[selectedId]) selectedId = null;

  // Le compteur suit le graphe : sans l'Artisan des dés, l'Abîme n'y est pas (A94).
  const shown = visibleNodes(game);
  // Un nœud masqué n'est pas cliquable : la sélection ne peut pas y rester.
  const revealed = revealedIds(ctx, shown);
  if (selectedId && !revealed.has(selectedId)) selectedId = null;
  const owned = shown.filter((n) => (game.state.nodes[n.id] ?? 0) > 0).length;
  const wrap = document.createElement('div');
  wrap.className = 'tree-tab';
  wrap.innerHTML = `
    <div class="tree-sub">
      <h2 class="tree-sub-title">Arbre des talents</h2>
      <span class="tree-sub-count" data-role="tree-count">${icon('node')}<b>${owned}</b> / ${shown.length} nœuds acquis</span>
    </div>
    <div class="tree-scroll" data-role="tree-scroll" tabindex="0" aria-label="Arbre des talents, défilement vertical"></div>
    <section class="node-panel f-panel f-panel--gold" data-role="node-panel" aria-label="Nœud sélectionné" aria-live="polite"></section>
  `;
  container.appendChild(wrap);

  const scroll = wrap.querySelector<HTMLElement>('[data-role="tree-scroll"]') as HTMLElement;
  const panel = wrap.querySelector<HTMLElement>('[data-role="node-panel"]') as HTMLElement;
  // Le flash du dernier achat ne joue qu'au premier dessin de ce rendu.
  let flashId = lastBoughtId;
  let fadeIn = lastBoughtWasNew;
  lastBoughtId = null;
  lastBoughtWasNew = false;

  let drawnWidth = 0;
  const draw = (): void => {
    const width = Math.max(300, Math.floor(scroll.clientWidth || 372));
    drawnWidth = width;
    scroll.innerHTML = graphHtml(ctx, width, flashId, fadeIn);
    flashId = null;
    fadeIn = false;
  };
  const drawPanel = (): void => {
    panel.innerHTML = panelHtml(ctx);
    panel.classList.toggle('node-panel--empty', !selectedId);
    // Plus d'invite et rien de sélectionné : le panneau se replie, l'arbre gagne la place.
    panel.classList.toggle('node-panel--quiet', !selectedId && isHintSeen('treeHint'));
  };
  draw();
  drawPanel();
  scroll.scrollTop = savedScroll;

  scroll.addEventListener('scroll', () => {
    savedScroll = scroll.scrollTop;
  });

  // Sélection : un appui sur un nœud (ou son étiquette) ouvre le panneau.
  scroll.addEventListener('click', (e) => {
    const target = e.target as HTMLElement | null;
    const hit = target?.closest<HTMLElement>('[data-node], [data-for]');
    const id = hit?.dataset.node ?? hit?.dataset.for;
    if (!id || !NODE_BY_ID[id]) return;
    selectedId = id;
    markHintSeen('treeHint');
    for (const btn of scroll.querySelectorAll<HTMLElement>('[data-node]')) {
      const on = btn.dataset.node === id;
      btn.classList.toggle('is-selected', on);
      btn.setAttribute('aria-pressed', String(on));
    }
    drawPanel();
  });

  panel.addEventListener('click', (e) => {
    const target = e.target as HTMLElement | null;
    const action = target?.closest<HTMLElement>('[data-action]')?.dataset.action;
    if (action === 'close-node') {
      const id = selectedId;
      selectedId = null;
      for (const btn of scroll.querySelectorAll<HTMLElement>('[data-node]')) {
        btn.classList.remove('is-selected');
        btn.setAttribute('aria-pressed', 'false');
      }
      drawPanel();
      if (id) scroll.querySelector<HTMLElement>(`[data-node="${id}"]`)?.focus({ preventScroll: true });
      return;
    }
    if (action === 'buy-node' && selectedId) {
      if (!game.canBuyNode(selectedId).ok) return;
      lastBoughtId = selectedId;
      lastBoughtWasNew = (game.state.nodes[selectedId] ?? 0) === 0;
      savedScroll = scroll.scrollTop;
      // L'événement `purchase` reconstruit tout l'écran (index.ts).
      game.buyNode(selectedId);
    }
  });

  // Largeur réelle (rotation, fenêtre redimensionnée) : on redessine le graphe.
  if (typeof ResizeObserver !== 'undefined') {
    const ro = new ResizeObserver(() => {
      if (!scroll.isConnected) {
        ro.disconnect();
        return;
      }
      const width = Math.max(300, Math.floor(scroll.clientWidth || 372));
      if (Math.abs(width - drawnWidth) >= 1) {
        const top = scroll.scrollTop;
        draw();
        scroll.scrollTop = top;
      }
    });
    ro.observe(scroll);
  }
}
